export type HeatLevel = 'healthy' | 'warming' | 'near_limit' | 'over_limit'

// Bands are fractions of the configured limit (MAX_PORTFOLIO_HEAT_PCT, served
// as max_heat_pct), so they move with it. At the default 10% limit they fall
// at 5% and 8%.
export const WARMING_AT = 0.5
export const NEAR_LIMIT_AT = 0.8

/**
 * Where total heat sits against the limit. Both values are fractions of the
 * account (0.05 = 5%), as the API returns them.
 *
 * "over_limit" starts strictly above the limit, matching the backend's
 * `warning: total_heat_pct > max_heat_pct`.
 */
export const heatLevel = (heat: number, max: number): HeatLevel => {
  if (!(max > 0)) return heat > 0 ? 'over_limit' : 'healthy'
  // Rounded so float noise doesn't move a boundary: 0.08 / 0.1 is 0.7999…
  const ratio = Math.round((heat / max) * 1e6) / 1e6
  if (ratio > 1) return 'over_limit'
  if (ratio >= NEAR_LIMIT_AT) return 'near_limit'
  if (ratio >= WARMING_AT) return 'warming'
  return 'healthy'
}

// `dark:` variants apply only on the Dashboard's dark terminal theme.
export const HEAT_STYLES: Record<HeatLevel, { label: string; text: string; bar: string; badge: string }> = {
  healthy: {
    label: 'healthy',
    text: 'text-green-700 dark:text-green-400',
    bar: 'bg-green-500 dark:bg-gradient-to-r dark:from-emerald-500 dark:to-green-400',
    badge: 'bg-green-50 text-green-700 dark:bg-green-500/15 dark:text-green-300',
  },
  warming: {
    label: 'warming',
    text: 'text-amber-700 dark:text-amber-400',
    bar: 'bg-amber-500 dark:bg-gradient-to-r dark:from-orange-500 dark:to-amber-400',
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  },
  near_limit: {
    label: 'near limit',
    text: 'text-red-700 dark:text-red-400',
    bar: 'bg-red-500 dark:bg-gradient-to-r dark:from-red-600 dark:to-rose-400',
    badge: 'bg-red-50 text-red-700 dark:bg-red-500/15 dark:text-red-300',
  },
  over_limit: {
    label: 'over limit',
    text: 'text-red-700 dark:text-red-400',
    bar: 'bg-red-600 dark:bg-gradient-to-r dark:from-red-700 dark:to-red-500',
    badge: 'bg-red-100 text-red-800 dark:bg-red-500/25 dark:text-red-200',
  },
}

/** 0.034 -> "3.4%" */
export const formatHeatPct = (fraction: number, decimals = 1): string =>
  `${(fraction * 100).toFixed(decimals)}%`
